import React, { useEffect, useState } from 'react';
import { SubTabs } from './kit';
import { type AdminTargetIn } from './adminNav';
import ForYouSettings from './foryou/Settings';
import ForYouUsage from './foryou/Usage';
import ForYouMatching from './foryou/Matching';
import WhatsNewNews from './whatsnew/News';
import WhatsNewSettings from './whatsnew/Settings';
import WhatsNewUsage from './whatsnew/Usage';
import FeatureAccessCard from './FeatureAccessCard';
import { NotEnabled, tabKey, useAdminAccess } from './adminAccess';

/**
 * /admin → For You & What's New (group key `foryou_whatsnew`).
 *
 * One group for the two member pages. Each tab is one entry of TABS:
 *   name    the label on screen (and, snake_cased, the deep-link tab value:
 *           /admin?group=foryou_whatsnew&tab=for_you_settings)
 *   render  the tab's content; tabs mount the first time they open and stay
 *           mounted, so a half-done edit survives a look elsewhere.
 *
 * For You's tabs are backed by Backend/foryou.py (panel section "foryou").
 * What's New appends its own entries to TABS (section "whatsnew").
 * Feature access (super admins only, "foryou_whatsnew.feature_access"): who
 * may open the new For You and What's New member pages (FeatureAccessCard,
 * Backend/feature_access.py). Other admins never get it, even with the group
 * open to them. A sub-tab that Access control keeps from this admin
 * ("foryou_whatsnew.<tab>") is left out of the strip; a deep link to it shows
 * the "not enabled" state.
 */

export interface PanelTab { name: string; render: () => React.ReactNode }

export const TABS: PanelTab[] = [
  { name: 'For You · Settings', render: () => <ForYouSettings /> },
  { name: 'For You · Usage', render: () => <ForYouUsage /> },
  { name: 'For You · Matching', render: () => <ForYouMatching /> },
  { name: "What's New · News", render: () => <WhatsNewNews /> },
  { name: "What's New · Settings", render: () => <WhatsNewSettings /> },
  { name: "What's New · Usage", render: () => <WhatsNewUsage /> },
  { name: 'Feature access', render: () => <FeatureAccessCard only={['foryou', 'whatsnew']} /> },
];

const ForYouWhatsNewPanel: React.FC<{ target?: AdminTargetIn }> = ({ target }) => {
  const access = useAdminAccess();
  const names = TABS.map(t => t.name);
  const allowed = names.filter(n => access.canTab('foryou_whatsnew', tabKey(n)));
  const [tab, setTab] = useState(allowed[0] || names[0]);
  useEffect(() => {
    if (!target?.tab) return;
    const want = tabKey(String(target.tab));
    const hit = names.find(n => tabKey(n) === want || n === target.tab);
    if (hit) setTab(hit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target?.nonce]);
  const [seen, setSeen] = useState<Record<string, boolean>>({ [names[0]]: true });
  useEffect(() => { setSeen(s => (s[tab] ? s : { ...s, [tab]: true })); }, [tab]);

  const blocked = !allowed.includes(tab);
  return (
    <div className="flex flex-col gap-4">
      <SubTabs tabs={allowed} active={blocked ? '' : tab} onChange={setTab} />
      {blocked && <NotEnabled what={tab} compact />}
      {TABS.map(t => (seen[t.name] && allowed.includes(t.name) ? (
        <div key={t.name} style={{ display: t.name === tab ? undefined : 'none' }}>{t.render()}</div>
      ) : null))}
    </div>
  );
};

export default ForYouWhatsNewPanel;
